import db from './db';
import { getStoreName, isOfflineSupported } from './endpointMapper';
import { idMapper } from './idMapper';
import { getRelationshipFields } from './dependencyGraph';
import { getUserIdFromToken as getUserIdFromJWT, isTokenExpired, isTokenValidLocally } from './jwtUtils';
import { deleteColumnOffline, deleteProjectOffline, findRecord } from './plannerProjectsOffline';

// ##### HELPER: Extract user_id from JWT token
function getUserIdFromToken() {
  const token = localStorage.getItem('access_token');
  return getUserIdFromJWT(token);
}

class OfflineInterceptor {
  // ##### HANDLE REQUEST (when offline)
  async handleRequest(config) {
    const { method, url } = config;
    const storeName = getStoreName(url);

    if (!storeName) {
      throw new Error(`Endpoint not supported offline: ${url}`);
    }

    if (method.toLowerCase() === 'get') {
      return this.handleGet(url, config.params, storeName);
    }

    if (method.toLowerCase() === 'delete' && storeName === 'planner_projects') {
      const itemId = this.extractIdFromUrl(url);
      await deleteProjectOffline(itemId);
      return { data: { id: itemId, deleted: true }, status: 200, fromCache: true, synced: false };
    }

    if (method.toLowerCase() === 'delete' && storeName === 'planner_project_columns') {
      const itemId = this.extractIdFromUrl(url);
      const purgeCards = ['1', 'true', 'yes'].includes(String(config.params?.delete_cards || '').toLowerCase());
      await deleteColumnOffline(itemId, { purgeCards });
      return { data: { id: itemId, deleted: true }, status: 200, fromCache: true, synced: false };
    }

    if (['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())) {
      return this.handleWrite(method, url, config.data, storeName);
    }

    throw new Error(`Method not supported offline: ${method}`);
  }

  // ##### HANDLE GET (read from IndexedDB)
  async handleGet(url, params, storeName) {
    const store = db[storeName];
    const token = localStorage.getItem('access_token');
    
    // ##### SECURITY: Check token validity locally
    if (!token) {
      throw new Error('User not authenticated');
    }

    // Check if token is expired (for offline access)
    const isExpired = isTokenExpired(token);
    const isOffline = !navigator.onLine;
    
    // If token expired and offline, allow limited access (read-only from cache)
    if (isExpired && isOffline) {
      console.warn('Token expired offline - allowing limited cache access');
      // Continue to allow reading from cache (limited access mode)
    } else if (isExpired && !isOffline) {
      // Token expired online → should be handled by refresh logic
      throw new Error('Token expired - please refresh');
    }

    const userId = getUserIdFromToken();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    let query = store.where('user_id').equals(userId);

    if (params?.date) {
      query = query.filter(item => item.date === params.date);
    }

    if (params?.flow_type) {
      query = query.filter(item => item.flow_type === params.flow_type);
    }

    if (params?.categ_l0) {
      query = query.filter(item => item.categ_l0 === params.categ_l0);
    }

    if (params?.project != null && (storeName === 'planner_project_columns' || storeName === 'planner_project_cards')) {
      const projectKey = String(params.project);
      query = query.filter((item) => String(item.project) === projectKey);
    }

    const data = await query.toArray();

    return {
      data: {
        results: data,
        count: data.length
      },
      status: 200,
      fromCache: true
    };
  }

  // ##### HANDLE WRITE (POST/PUT/DELETE - save locally + queue)
  async handleWrite(method, url, data, storeName) {
    const store = db[storeName];
    const token = localStorage.getItem('access_token');
    
    // ##### SECURITY: Block writes if token expired offline
    if (!token) {
      throw new Error('User not authenticated');
    }

    const isExpired = isTokenExpired(token);
    const isOffline = !navigator.onLine;

    // If token expired and offline → block writes (security)
    if (isExpired && isOffline) {
      throw new Error('Token expired offline - write operations blocked for security');
    }

    const userId = getUserIdFromToken();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // ##### TRY TO RESOLVE RELATIONSHIPS (if mappings already exist)
    let dataToSave = { ...data };
    const relationshipFields = getRelationshipFields(storeName);
    if (relationshipFields.length > 0) {
      // Try to resolve temp IDs in relationships (non-blocking)
      // If not resolved yet, will be resolved during sync
      dataToSave = await idMapper.resolveRelationships(dataToSave, storeName, relationshipFields);
    }

    let result;
    const now = new Date().toISOString();

    if (method.toLowerCase() === 'post') {
      const itemToSave = {
        ...dataToSave,
        id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: userId,
        created_at: now, // ##### FIX: Add created_at timestamp
        updated_at: now, // ##### FIX: Add updated_at timestamp
        synced: false
      };
      await store.add(itemToSave);
      result = itemToSave;
    }

    if (['put', 'patch'].includes(method.toLowerCase())) {
      const itemId = data?.id ?? this.extractIdFromUrl(url);
      const existingItem = await findRecord(store, itemId);
      const itemToUpdate = {
        ...existingItem,
        ...dataToSave,
        id: existingItem?.id ?? itemId,
        user_id: userId,
        created_at: existingItem?.created_at || now,
        updated_at: now,
        local_updated_at: now,
        synced: false
      };
      await store.put(itemToUpdate);
      result = itemToUpdate;
    }

    if (method.toLowerCase() === 'delete') {
      const itemId = data?.id || this.extractIdFromUrl(url);
      const existingItem = itemId != null ? await findRecord(store, itemId) : null;
      if (itemId) {
        await store.delete(existingItem?.id ?? itemId);
        result = { id: existingItem?.id ?? itemId, deleted: true };
      } else {
        throw new Error('Cannot delete: ID not found');
      }
    }

    await db.sync_queue.add({
      method: method.toUpperCase(),
      url,
      data: result,
      timestamp: Date.now(),
      retries: 0,
      status: 'pending',
      conflict_status: 'none'
    });

    // ##### FIX: Resolve relationships for response (e.g., cluster_id -> cluster object)
    let finalResult = { ...result };
    if (relationshipFields.length > 0 && storeName === 'notes' && result.cluster_id) {
      // Resolve cluster_id to cluster object
      try {
        const clusterStore = db.notepad_clusters;
        const cluster = await clusterStore.get(result.cluster_id);
        if (cluster) {
          finalResult.cluster = {
            id: cluster.id,
            name: cluster.name,
            color: cluster.color,
            created_at: cluster.created_at,
            updated_at: cluster.updated_at
          };
        }
      } catch (error) {
        console.warn('Failed to resolve cluster for note:', error);
        // Continue without cluster object
      }
    }

    return {
      data: finalResult,
      status: 200,
      fromCache: true,
      synced: false
    };
  }

  // ##### CACHE RESPONSE (when online - save to IndexedDB)
  async cacheResponse(config, responseData) {
    if (config.method?.toLowerCase() !== 'get') return;

    const storeName = getStoreName(config.url);
    if (!storeName) return;

    try {
      const store = db[storeName];
      const userId = getUserIdFromToken();
      if (!userId) return;

      const data = responseData.results || responseData;

      if (Array.isArray(data)) {
        // Get all current items for this user to identify deleted items
        const currentItems = await store.where('user_id').equals(userId).toArray();
        const currentItemIds = new Set(currentItems.map(item => item.id));
        const newItemIds = new Set(data.map(item => item.id));

        // Update/create items from response
        for (const item of data) {
          const itemToSave = {
            ...item,
            user_id: userId, // CRITICAL: Ensure user_id is set
            updated_at: item.updated_at || item.created_at,
            local_updated_at: null,
            synced: true
          };
          await store.put(itemToSave);
        }

        // Remove items that are no longer in the response (deleted on server)
        // Only remove items that were synced (not local-only items)
        const itemsToDelete = currentItems.filter(item => 
          !newItemIds.has(item.id) && item.synced === true
        );
        
        for (const itemToDelete of itemsToDelete) {
          await store.delete(itemToDelete.id);
        }
      } else if (data && typeof data === 'object') {
        const itemToSave = {
          ...data,
          user_id: userId, // CRITICAL: Ensure user_id is set
          updated_at: data.updated_at || data.created_at,
          local_updated_at: null,
          synced: true
        };
        await store.put(itemToSave);
      }
    } catch (error) {
      console.error('Failed to cache response:', error);
    }
  }

  // ##### GET FROM CACHE (fallback when error offline)
  async getFromCache(config) {
    const storeName = getStoreName(config.url);
    if (!storeName) return null;

    try {
      return await this.handleGet(config.url, config.params, storeName);
    } catch (error) {
      console.error('Failed to get from cache:', error);
      return null;
    }
  }

  // ##### GET FROM CACHE FIRST (always try cache first, even when online)
  async getFromCacheFirst(url, params, storeName) {
    try {
      return await this.handleGet(url, params, storeName);
    } catch (error) {
      console.error('Failed to get from cache first:', error);
      return null;
    }
  }

  // ##### HELPER: Extract ID from URL
  extractIdFromUrl(url) {
    if (!url) return null;
    const path = url.split('?')[0];
    const matches = path.match(/\/(\d+|temp_[A-Za-z0-9_]+)\/?$/);
    if (!matches) return null;
    const raw = matches[1];
    if (raw.startsWith('temp_')) return raw;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : raw;
  }
}

export const offlineInterceptor = new OfflineInterceptor();
export default offlineInterceptor;
